import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import {
  studentFormsApi,
  type StudentFormField,
  type StudentFormFieldAccess,
  type StudentFormFieldType,
} from '@/api/studentForms'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'

const FIELD_TYPES: StudentFormFieldType[] = [
  'text',
  'textarea',
  'select',
  'multiselect',
  'date',
  'phone',
  'email',
  'url',
  'boolean',
  'rating',
  'language_pair',
]

const ACCESS_LEVELS: StudentFormFieldAccess[] = [
  'teacher_only',
  'student_visible',
  'student_editable',
]

interface StudentFormOwnFieldDialogProps {
  open: boolean
  /** null — заводим новое поле. */
  field: StudentFormField | null
  onClose: () => void
  onSaved: (fields: StudentFormField[]) => void
}

/**
 * Собственное поле анкеты учителя.
 *
 * Ключ не спрашиваем — он генерируется из названия на бэкенде. Обязательность
 * тоже не здесь: её переключают прямо в списке полей, одинаково для системных
 * и собственных.
 */
export function StudentFormOwnFieldDialog({
  open,
  field,
  onClose,
  onSaved,
}: StudentFormOwnFieldDialogProps) {
  const { t } = useTranslation()

  const [label, setLabel] = useState('')
  const [type, setType] = useState<StudentFormFieldType>('text')
  const [optionsText, setOptionsText] = useState('')
  const [access, setAccess] = useState<StudentFormFieldAccess>('teacher_only')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLabel(field?.label ?? '')
    setType(field?.type ?? 'text')
    setOptionsText((field?.options ?? []).join('\n'))
    setAccess(field?.access ?? 'teacher_only')
    setError(null)
  }, [open, field])

  const needsOptions = type === 'select' || type === 'multiselect'

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload = {
        label: label.trim(),
        type,
        access,
        ...(needsOptions
          ? {
              options: optionsText
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
            }
          : {}),
      }

      const fields = field
        ? await studentFormsApi.updateOwnField(field.id, payload)
        : await studentFormsApi.createOwnField(payload)

      onSaved(fields)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('studentForms.saveFieldError')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {field ? t('studentForms.editOwnField') : t('studentForms.createOwnField')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('studentForms.fieldLabel')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
            required
            helperText={t('studentForms.keyAutoHint')}
          />

          <FormControl size="small" fullWidth>
            <InputLabel id="student-form-own-field-type">{t('studentForms.fieldType')}</InputLabel>
            <Select
              labelId="student-form-own-field-type"
              label={t('studentForms.fieldType')}
              value={type}
              onChange={(e) => setType(e.target.value as StudentFormFieldType)}
            >
              {FIELD_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`studentForms.fieldTypes.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {needsOptions && (
            <TextField
              label={t('studentForms.fieldOptions')}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
              helperText={t('studentForms.fieldOptionsHint')}
            />
          )}

          <FormControl size="small" fullWidth>
            <InputLabel id="student-form-own-field-access">
              {t('studentForms.fieldAccess')}
            </InputLabel>
            <Select
              labelId="student-form-own-field-access"
              label={t('studentForms.fieldAccess')}
              value={access}
              onChange={(e) => setAccess(e.target.value as StudentFormFieldAccess)}
            >
              {ACCESS_LEVELS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`studentForms.accessLevels.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={saving || label.trim() === '' || (needsOptions && optionsText.trim() === '')}
          onClick={() => void handleSave()}
        >
          {saving ? t('common.saving', 'Сохранение...') : t('common.save', 'Сохранить')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
